import { useCallback, useEffect, useState } from 'react';
import { useCompanyApi } from '@lendica/hooks';
import { v2 } from '@lendica/api';
import { Payment } from '@lendica/utils';

interface UsePaginatedPaymentsOptions {
    pageSize?: number;
    mock?: boolean;
    mockTotal?: number;
}

export const usePaginatedPayments = (options: UsePaginatedPaymentsOptions = {}) => {
    const { pageSize = 20, mock = false, mockTotal = 500 } = options;
    const api = useCompanyApi();

    const [payments, setPayments] = useState<Payment[]>([]);
    const [page, setPage] = useState(1);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasNext, setHasNext] = useState(true);

    const fetchPage = useCallback(
        async (nextPage: number) => {
            if (mock) {
                const mod = await import('../test/mockPayments');
                return mod.getMockPaginated({ page: nextPage, pageSize, total: mockTotal });
            }
            const resp = await api.get<v2.PaginatedResponse<Payment>>('/payments/list', {
                params: { page: nextPage, page_size: pageSize },
            });
            return resp;
        },
        [api, pageSize, mock, mockTotal]
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setIsInitialLoading(true);
                setPayments([]);
                const resp = await fetchPage(1);
                if (cancelled) return;
                setPayments(resp.results);
                setHasNext(Boolean(resp.next));
                setPage(1);
            } finally {
                if (!cancelled) setIsInitialLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [fetchPage]);

    const loadMore = useCallback(async () => {
        if (isInitialLoading || isFetchingMore || !hasNext) return;
        setIsFetchingMore(true);
        try {
            const nextPage = page + 1;
            const resp = await fetchPage(nextPage);
            setPayments(prev => [...prev, ...resp.results]);
            setHasNext(Boolean(resp.next));
            setPage(nextPage);
        } finally {
            setIsFetchingMore(false);
        }
    }, [fetchPage, hasNext, isFetchingMore, isInitialLoading, page]);

    return {
        payments,
        isInitialLoading,
        isFetchingMore,
        hasNext,
        loadMore,
    };
};
